import { useCallback, useState } from 'react'

interface UsePlaySelectionReturn {
	selectedPlays: Set<string>
	togglePlaySelection: (playId: string) => void
	selectAllVisiblePlays: (playIds: string[]) => void
	clearSelection: () => void
	selectedCount: number
	hasSelection: boolean
}

/**
 * Hook for managing play selection state
 *
 * Provides selection logic for multi-select operations
 * like batch export or batch delete functionality.
 */
export function usePlaySelection(): UsePlaySelectionReturn {
	const [selectedPlays, setSelectedPlays] = useState<Set<string>>(new Set())

	const togglePlaySelection = useCallback((playId: string) => {
		setSelectedPlays(prev => {
			const newSelection = new Set(prev)
			if (newSelection.has(playId)) {
				newSelection.delete(playId)
			} else {
				newSelection.add(playId)
			}
			return newSelection
		})
	}, [])

	const selectAllVisiblePlays = useCallback((playIds: string[]) => {
		setSelectedPlays(prev => {
			const allCurrentlySelected = playIds.every(id => prev.has(id))
			const newSelection = new Set(prev)

			for (const id of playIds) {
				if (allCurrentlySelected) {
					newSelection.delete(id)
				} else {
					newSelection.add(id)
				}
			}

			return newSelection
		})
	}, [])

	const clearSelection = useCallback(() => {
		setSelectedPlays(new Set())
	}, [])

	return {
		selectedPlays,
		togglePlaySelection,
		selectAllVisiblePlays,
		clearSelection,
		selectedCount: selectedPlays.size,
		hasSelection: selectedPlays.size > 0,
	}
}
